import React, { useEffect, useState } from 'react';
import { 
  TableBody, 
  TableCell, 
  TableHead, 
  TableRow, 
  Paper, 
  Typography, 
  Box, 
  Chip, 
  Card, 
  CardContent,
  Grid,
  Switch,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip
} from '@material-ui/core';
import { Table, WarningIcon } from '@backstage/core-components';
import { Alert } from '@material-ui/lab';
import { Page, Header, Content, HeaderLabel } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { appConfigApiRef, FeatureToggle } from '../../apis';

const TABLE_OPTIONS = {
  paging: false,
  search: true,
  searchFieldAlignment: 'right',
  searchFieldVariant: 'outlined',
  searchText: '',
  toolbar: true,
  showTitle: true,
  showFirstLastPageButtons: false,
  showSelectAllCheckbox: false,
  showTextRowsSelected: false,
  toolbarButtonAlignment: 'left',
  searchFieldStyle: { width: '410px' }
} as const;

export const FeatureTogglesDashboard = () => {
  const appConfigApi = useApi(appConfigApiRef);
  const [featureToggles, setFeatureToggles] = useState<FeatureToggle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'no-filter' | 'recent' | 'environment-mismatch'>('no-filter');
  const [summaryFilter, setSummaryFilter] = useState<{ type: 'enabled' | 'disabled', environment?: string } | null>(null);

  const environments = ['test', 'acceptance', 'production'];

  const formatDate = (date: Date): string => {
    return date.toLocaleString('da-DK', {
      timeZone: 'Europe/Copenhagen',
      dateStyle: 'short',
      timeStyle: 'medium'
    });
  };

  const isRecent = (lastModified?: Date) => {
    if (!lastModified) return false;
    const now = new Date();
    const diff = now.getTime() - lastModified.getTime();
    return diff <= 24 * 60 * 60 * 1000; // 24 hours in ms
  };

  const hasEnvironmentMismatch = (key: string) => {
    const togglesForKey = featureToggles.filter(toggle => toggle.key === key);
    
    // Check if production is enabled but lower environments are not
    const productionToggle = togglesForKey.find(t => t.environment === 'production');
    const acceptanceToggle = togglesForKey.find(t => t.environment === 'acceptance');
    const testToggle = togglesForKey.find(t => t.environment === 'test');
    
    // Production enabled but acceptance disabled or missing
    if (productionToggle?.enabled && (!acceptanceToggle?.enabled || !acceptanceToggle)) {
      return true;
    }
    
    // Production enabled but test disabled or missing
    if (productionToggle?.enabled && (!testToggle?.enabled || !testToggle)) {
      return true;
    }
    
    // Acceptance enabled but test disabled or missing
    if (acceptanceToggle?.enabled && (!testToggle?.enabled || !testToggle)) {
      return true;
    }
    
    return false;
  };

  const getEnvironmentMismatchDetails = (key: string) => {
    const togglesForKey = featureToggles.filter(toggle => toggle.key === key);
    
    const productionToggle = togglesForKey.find(t => t.environment === 'production');
    const acceptanceToggle = togglesForKey.find(t => t.environment === 'acceptance');
    const testToggle = togglesForKey.find(t => t.environment === 'test');
    
    const issues = [];
    
    if (productionToggle?.enabled && (!acceptanceToggle?.enabled || !acceptanceToggle)) {
      issues.push('Production enabled but acceptance disabled/missing');
    }
    
    if (productionToggle?.enabled && (!testToggle?.enabled || !testToggle)) {
      issues.push('Production enabled but test disabled/missing');
    }
    
    if (acceptanceToggle?.enabled && (!testToggle?.enabled || !testToggle)) {
      issues.push('Acceptance enabled but test disabled/missing');
    }
    
    return issues.join(', ');
  };

  useEffect(() => {
    const fetchFeatureToggles = async () => {
      try {
        setLoading(true);
        setError(null);
        const toggles = await appConfigApi.getFeatureToggles();
        setFeatureToggles(toggles);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch feature toggles');
      } finally {
        setLoading(false);
      }
    };

    fetchFeatureToggles();
    // Refresh every 5 minutes
    const interval = setInterval(fetchFeatureToggles, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [appConfigApi]);

  // Build a map of feature keys to their environment toggles
  const featureMap: Record<string, Record<string, FeatureToggle | undefined>> = {};
  featureToggles.forEach(toggle => {
    if (!featureMap[toggle.key]) featureMap[toggle.key] = {};
    featureMap[toggle.key][toggle.environment] = toggle;
  });
  const featureKeys = Object.keys(featureMap).sort();

  const getEnvironmentSummary = (environment: string) => {
    const toggles = featureToggles.filter(toggle => toggle.environment === environment);
    const enabled = toggles.filter(toggle => toggle.enabled).length;
    const total = toggles.length;
    return { enabled, total, disabled: total - enabled };
  };

  const shouldShowFeature = (key: string) => {
    // Check summary filter first
    if (summaryFilter) {
      if (summaryFilter.type === 'enabled') {
        if (summaryFilter.environment) {
          // Filter by specific environment enabled toggles
          const toggle = featureMap[key][summaryFilter.environment];
          return toggle?.enabled === true;
        } else {
          // Filter by any environment enabled toggles
          return environments.some(env => {
            const toggle = featureMap[key][env];
            return toggle?.enabled === true;
          });
        }
      }
      if (summaryFilter.type === 'disabled') {
        if (summaryFilter.environment) {
          // Filter by specific environment disabled toggles
          const toggle = featureMap[key][summaryFilter.environment];
          return toggle?.enabled === false;
        } else {
          // Filter by any environment disabled toggles
          return environments.some(env => {
            const toggle = featureMap[key][env];
            return toggle?.enabled === false;
          });
        }
      }
    }

    if (filter === 'no-filter') return true;
    
    if (filter === 'recent') {
      const togglesForKey = featureToggles.filter(toggle => toggle.key === key);
      return togglesForKey.some(toggle => isRecent(toggle.lastModified));
    }
    
    if (filter === 'environment-mismatch') {
      return hasEnvironmentMismatch(key);
    }
    
    return true;
  };

  const handleSummaryCardClick = (type: 'enabled' | 'disabled', environment?: string) => {
    // If clicking the same filter that's already active, clear it
    if (summaryFilter?.type === type && summaryFilter?.environment === environment) {
      setSummaryFilter(null);
    } else {
      setSummaryFilter({ type, environment });
    }
    setFilter('no-filter'); // Clear other filters
  };

  const clearSummaryFilter = () => {
    setSummaryFilter(null);
  };

  const isCardActive = (type: 'enabled' | 'disabled', environment?: string) => {
    return summaryFilter?.type === type && summaryFilter?.environment === environment;
  };

  const filteredFeatureKeys = featureKeys.filter(shouldShowFeature);

  // Create table data structure
  const tableData = filteredFeatureKeys.map(key => {
    const row: any = { featureKey: key };
    environments.forEach(env => {
      const toggle = featureMap[key][env];
      row[env] = toggle || null;
    });
    return row;
  });

  if (loading) {
    return (
      <Page themeId="tool">
        <Header title="Feature Toggles Dashboard">
          <HeaderLabel label="Status" value="Loading..." />
        </Header>
        <Content>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        </Content>
      </Page>
    );
  }

  if (error) {
    return (
      <Page themeId="tool">
        <Header title="Feature Toggles Dashboard">
          <HeaderLabel label="Status" value="Error" />
        </Header>
        <Content>
          <Alert severity="error">{error}</Alert>
        </Content>
      </Page>
    );
  }

  return (
    <Page themeId="tool">
      <Header title="Feature Toggles Dashboard">
      </Header>
      <Content>
        <Grid container spacing={3} style={{ marginBottom: '24px' }}>
          {environments.map((env) => {
            const summary = getEnvironmentSummary(env);
            return (
              <Grid item xs={12} sm={4} key={env}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" component="h2" style={{ textTransform: 'capitalize' }}>
                      {env}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Total: {summary.total}
                    </Typography>
                    <Box display="flex" marginTop={1} style={{ gap: '8px', flexWrap: 'wrap' }}>
                      <Chip 
                        label={`Enabled: ${summary.enabled}`} 
                        color={isCardActive('enabled', env) ? "default" : "primary"}
                        size="small"
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleSummaryCardClick('enabled', env)}
                      />
                      <Chip 
                        label={`Disabled: ${summary.disabled}`} 
                        color={isCardActive('disabled', env) ? "default" : "secondary"}
                        size="small"
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleSummaryCardClick('disabled', env)}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '16px' }}>
          {summaryFilter && (
            <Chip 
              label={`Clear filter: ${summaryFilter.type} ${summaryFilter.environment || ''}`}
              color="default"
              onDelete={clearSummaryFilter}
              style={{ cursor: 'pointer' }}
            />
          )}
          <FormControl variant="outlined" style={{ minWidth: 300 }}>
            <InputLabel>Filter feature toggles</InputLabel>
            <Select
              value={filter}
              onChange={(e) => setFilter(e.target.value as 'no-filter' | 'recent' | 'environment-mismatch')}
              disabled={!!summaryFilter}
            >
              <MenuItem value="no-filter">No filtering</MenuItem>
              <MenuItem value="recent">Recent changes (last 24 hours)</MenuItem>
              <MenuItem value="environment-mismatch">Environment mismatches (enabled in higher env, disabled in lower)</MenuItem>
            </Select>
          </FormControl>
        </div>

        <Paper>
          <Table
            title="Feature Toggles"
            options={TABLE_OPTIONS}
            columns={[
              {
                title: 'Feature Key',
                field: 'featureKey',
                render: (row: any) => (
                  <Box display="flex" alignItems="center" style={{ gap: '4px' }}>
                    <Typography variant="body2" style={{ fontFamily: 'monospace' }}>
                      {row.featureKey}
                    </Typography>
                    {hasEnvironmentMismatch(row.featureKey) && (
                      <Tooltip title={getEnvironmentMismatchDetails(row.featureKey)} arrow>
                        <span style={{ color: 'orange', display: 'flex', alignItems: 'center' }}>
                          <WarningIcon fontSize="small" />
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                )
              },
              ...environments.map(env => ({
                title: env.charAt(0).toUpperCase() + env.slice(1),
                field: env,
                align: 'center' as const,
                render: (row: any) => {
                  const toggle = row[env];
                  
                  // Determine if this cell should be grayed out based on summary filter
                  let cellStyle = {};
                  if (summaryFilter) {
                    if (summaryFilter.type === 'enabled') {
                      if (summaryFilter.environment) {
                        // Filter by specific environment enabled toggles
                        if (env !== summaryFilter.environment || !toggle?.enabled) {
                          cellStyle = { opacity: 0.3 };
                        }
                      } else {
                        // Filter by any environment enabled toggles
                        if (!toggle?.enabled) {
                          cellStyle = { opacity: 0.3 };
                        }
                      }
                    } else if (summaryFilter.type === 'disabled') {
                      if (summaryFilter.environment) {
                        // Filter by specific environment disabled toggles
                        if (env !== summaryFilter.environment || toggle?.enabled !== false) {
                          cellStyle = { opacity: 0.3 };
                        }
                      } else {
                        // Filter by any environment disabled toggles
                        if (toggle?.enabled !== false) {
                          cellStyle = { opacity: 0.3 };
                        }
                      }
                    }
                  } else if (filter === 'recent') {
                    // Gray out cells that are not recent
                    if (!toggle?.lastModified || !isRecent(toggle.lastModified)) {
                      cellStyle = { opacity: 0.3 };
                    }
                  } else if (filter === 'environment-mismatch') {
                    // Gray out cells that are not part of the mismatch
                    const hasMismatch = hasEnvironmentMismatch(row.featureKey);
                    if (!hasMismatch) {
                      cellStyle = { opacity: 0.3 };
                    } else {
                      // For mismatched toggles, highlight the problematic environments
                      const togglesForKey = featureToggles.filter(toggle => toggle.key === row.featureKey);
                      const productionToggle = togglesForKey.find(t => t.environment === 'production');
                      const acceptanceToggle = togglesForKey.find(t => t.environment === 'acceptance');
                      const testToggle = togglesForKey.find(t => t.environment === 'test');
                      
                      // Check if this environment should be highlighted as problematic
                      let isProblematic = false;
                      
                      // If production is enabled but acceptance is disabled/missing, acceptance is problematic
                      if (productionToggle?.enabled && (!acceptanceToggle?.enabled || !acceptanceToggle)) {
                        if (env === 'acceptance') {
                          isProblematic = true;
                        }
                      }
                      // If production is enabled but test is disabled/missing, test is problematic
                      if (productionToggle?.enabled && (!testToggle?.enabled || !testToggle)) {
                        if (env === 'test') {
                          isProblematic = true;
                        }
                      }
                      // If acceptance is enabled but test is disabled/missing, test is problematic
                      if (acceptanceToggle?.enabled && (!testToggle?.enabled || !testToggle)) {
                        if (env === 'test') {
                          isProblematic = true;
                        }
                      }
                      
                      // If this environment is not problematic, gray it out
                      if (!isProblematic) {
                        cellStyle = { opacity: 0.3 };
                      }
                    }
                  }
                  
                  if (toggle) {
                    return (
                      <Box display="flex" flexDirection="column" alignItems="center" style={cellStyle}>
                        <Switch 
                          checked={toggle.enabled} 
                          disabled 
                          color="primary"
                          style={{ 
                            color: toggle.enabled ? '#007d82' : '#f50057'
                          }}
                        />
                        {toggle.lastModified && (
                          <Typography variant="caption" style={{ marginTop: 2 }}>
                            Ændret: {formatDate(toggle.lastModified)}
                          </Typography>
                        )}
                      </Box>
                    );
                  }
                  return (
                    <Typography variant="caption" color="textSecondary" style={cellStyle}>-</Typography>
                  );
                }
              }))
            ]}
            data={tableData}
          />
        </Paper>
      </Content>
    </Page>
  );
}; 